/**
 * Convert RGB to HSL color space
 */
export function rgbToHsl(
	r: number,
	g: number,
	b: number,
): [number, number, number] {
	const rNorm = r / 255;
	const gNorm = g / 255;
	const bNorm = b / 255;

	const max = Math.max(rNorm, gNorm, bNorm);
	const min = Math.min(rNorm, gNorm, bNorm);
	let h = 0;
	let s = 0;
	const l = (max + min) / 2;

	if (max !== min) {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

		switch (max) {
			case rNorm:
				h = (gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0);
				break;
			case gNorm:
				h = (bNorm - rNorm) / d + 2;
				break;
			case bNorm:
				h = (rNorm - gNorm) / d + 4;
				break;
		}
		h /= 6;
	}

	return [h * 360, s, l];
}

/**
 * Convert HSL to RGB color space
 */
export function hslToRgb(
	h: number,
	s: number,
	l: number,
): [number, number, number] {
	const normalizedH = h / 360;

	const hue2rgb = (p: number, q: number, t: number) => {
		let adjustedT = t;
		if (adjustedT < 0) adjustedT += 1;
		if (adjustedT > 1) adjustedT -= 1;
		if (adjustedT < 1 / 6) return p + (q - p) * 6 * adjustedT;
		if (adjustedT < 1 / 2) return q;
		if (adjustedT < 2 / 3) return p + (q - p) * (2 / 3 - adjustedT) * 6;
		return p;
	};

	let r: number;
	let g: number;
	let b: number;

	if (s === 0) {
		r = l;
		g = l;
		b = l; // achromatic
	} else {
		const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
		const p = 2 * l - q;
		r = hue2rgb(p, q, normalizedH + 1 / 3);
		g = hue2rgb(p, q, normalizedH);
		b = hue2rgb(p, q, normalizedH - 1 / 3);
	}

	return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}
